import type { ObjectSchema } from 'yup';
import * as yup from 'yup';

import type {
  GroupResultDTO,
  UserResultDTO,
} from '@nora-monorepo/nora-api-client';

import type { UsersAndGroups } from '#ui-shared/components/member-picker/member-picker-types';

type Translate = (key: string, params?: unknown) => string;

/**
 * This is a factory function that creates a Yup schema for validating a list of operation members (users and groups) based on the provided translation function, admin role IDs, and allowed role IDs.
 *
 * @param t
 * @param adminRoleIds a list of valid admin role ids in NORA. This will be the basis
 * for a check to ensure at least one admin is present in the member list.
 * @param allowedRoleIds a list of allowed role ids in NORA. This will be the basis for a check to ensure that all members have valid roles.
 */
export function createOperationMemberListSchema(
  t: Translate,
  adminRoleIds: Set<string>,
  allowedRoleIds?: Set<string>,
): ObjectSchema<UsersAndGroups> {
  const adminRoleRequiredMessage = t('operation.validation.adminRoleRequired');
  const invalidRoleSelectedMessage = t('operation.validation.invalidRoleSelected');

  const isAdmin = (member?: { role: string }): boolean => !!member && adminRoleIds.has(member.role);
  const hasValidRole = (member?: { role: string }): boolean => {
    if (!member) {
      return false;
    }

    if (!allowedRoleIds) {
      return true;
    }

    return allowedRoleIds.has(member.role);
  };

  return yup.object({
    users: yup.array().of(
      yup.object({
        user: yup.mixed<UserResultDTO>().required(),
        role: yup.string().required(),
      }),
    ).required().default([]),
    groups: yup.array().of(
      yup.object({
        group: yup.mixed<GroupResultDTO>().required(),
        role: yup.string().required(),
      }),
    ).required().default([]),
  })
    .test('has-admin', adminRoleRequiredMessage, value => (value.users?.some(isAdmin)) || (value.groups?.some(isAdmin)))
    .test('valid-member-roles', invalidRoleSelectedMessage, (value) => {
      if (!value) {
        return false;
      }

      const usersHaveValidRoles = value.users.every(hasValidRole);
      const groupsHaveValidRoles = value.groups.every(hasValidRole);

      return usersHaveValidRoles && groupsHaveValidRoles;
    });
}
